import { Button, Label, Modal, Textarea, TextInput } from "flowbite-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ClientGroup } from "@/sharedTypes";

interface ClientGroupFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ClientGroup) => void;
  mode?: "create" | "edit";
  initialData?: ClientGroup;
}

const ClientGroupFormDialog = ({
  open,
  onClose,
  onSubmit,
  mode = "create",
  initialData,
}: ClientGroupFormDialogProps) => {
  const { t } = useTranslation(["clientGroup", "common"]);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ClientGroup>();

  useEffect(() => {
    if (open) {
      if (initialData) {
        reset(initialData);
      } else {
        reset({ group_name: "", group_description: "" } as ClientGroup);
      }
    }
  }, [initialData, reset, open]);

  const onFormSubmit = (data: ClientGroup) => {
    onSubmit(data);
  };

  return (
    <Modal show={open} onClose={onClose} size="md">
      <Modal.Header>
        {mode === "edit"
          ? t("clientGroup:editClientGroup")
          : t("clientGroup:createClientGroup")}
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          <div>
            <div className="mb-2 block">
              <Label htmlFor="group_name" value={t("clientGroup:groupName")} />
            </div>
            <TextInput
              id="group_name"
              placeholder={t("clientGroup:enterGroupName")}
              {...register("group_name", {
                required: t("clientGroup:groupNameRequired"),
              })}
              color={errors.group_name ? "failure" : undefined}
              helperText={
                errors.group_name && (
                  <span className="font-medium">
                    {errors.group_name.message}
                  </span>
                )
              }
            />
          </div>

          <div>
            <div className="mb-2 block">
              <Label
                htmlFor="group_description"
                value={t("clientGroup:description")}
              />
            </div>
            <Textarea
              id="group_description"
              placeholder={t("clientGroup:enterDescription")}
              {...register("group_description")}
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button color="failure" onClick={onClose}>
              {t("common:cancel")}
            </Button>
            <Button type="submit">
              {mode === "edit"
                ? t("common:saveChanges")
                : t("clientGroup:createClientGroup")}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default ClientGroupFormDialog;
